import { useId, useMemo, useState, type ReactNode } from 'react';
import { Card, SegmentedControl, cx } from './primitives';

/**
 * Graphiques en SVG pur, sans bibliothèque.
 *
 * Règles appliquées à tous les graphiques de l'application :
 *  - une seule échelle de valeurs (jamais deux axes y) ;
 *  - marques fines : colonnes ≤ 24 px, extrémité arrondie 4 px côté valeur,
 *    lignes de 2 px, points ≥ 8 px cerclés de la couleur de surface ;
 *  - écart de 2 px en couleur de surface entre segments accolés ;
 *  - grille en filet 1 px, jamais en pointillés ;
 *  - légende dès deux séries, étiquettes directes parcimonieuses ;
 *  - vue tableau accessible pour chaque graphique (les teintes claires de la
 *    palette ne passent pas 3:1 sur fond blanc : le tableau est la contrepartie).
 */

const SERIES_VARS = [
  'var(--viz-1)',
  'var(--viz-2)',
  'var(--viz-3)',
  'var(--viz-4)',
  'var(--viz-5)',
  'var(--viz-6)',
  'var(--viz-7)',
  'var(--viz-8)',
] as const;

/** Couleur de la série `index`. Les emplacements ne sont jamais recyclés. */
export function seriesColor(index: number): string {
  return SERIES_VARS[index] ?? 'var(--gray)';
}

export const SEQUENTIAL_VARS = [
  'var(--viz-seq-1)',
  'var(--viz-seq-2)',
  'var(--viz-seq-3)',
  'var(--viz-seq-4)',
  'var(--viz-seq-5)',
  'var(--viz-seq-6)',
  'var(--viz-seq-7)',
] as const;

/** Étape de la rampe séquentielle correspondant à `value` dans [0, max]. */
export function sequentialColor(value: number, max: number): string {
  if (max <= 0) return SEQUENTIAL_VARS[0];
  const ratio = Math.min(1, Math.max(0, value / max));
  const step = Math.min(SEQUENTIAL_VARS.length - 1, Math.floor(ratio * SEQUENTIAL_VARS.length));
  return SEQUENTIAL_VARS[step] ?? SEQUENTIAL_VARS[0];
}

// ---------------------------------------------------------------------------
// Échelles & aides
// ---------------------------------------------------------------------------

/** Bornes d'axe arrondies à un pas « propre » (1, 2, 2,5 ou 5 × 10ⁿ). */
export function niceScale(max: number, ticks = 4): { max: number; step: number; values: number[] } {
  if (max <= 0) return { max: 1, step: 1, values: [0, 1] };
  const rough = max / ticks;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / magnitude;
  const step = (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10) * magnitude;
  const niceMax = Math.ceil(max / step) * step;
  const values: number[] = [];
  for (let v = 0; v <= niceMax + step / 2; v += step) values.push(Math.round(v * 1000) / 1000);
  return { max: niceMax, step, values };
}

export function formatCompact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)} M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(abs >= 10_000 ? 0 : 1)} k`;
  return new Intl.NumberFormat('fr-BE', { maximumFractionDigits: 1 }).format(value);
}

// ---------------------------------------------------------------------------
// Cadre commun : titre, légende, bascule tableau
// ---------------------------------------------------------------------------

export interface ChartSeries {
  label: string;
  color?: string;
}

export interface ChartFrameProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  series: ChartSeries[];
  /** Rendu du tableau équivalent, obligatoire pour l'accessibilité. */
  table: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  height?: number;
}

/**
 * Enveloppe chaque graphique : en-tête, légende (dès deux séries), et bascule
 * graphique/tableau. Le tableau n'est pas une option : c'est le canal de secours
 * quand la couleur ne suffit pas.
 */
export function ChartFrame({
  title,
  subtitle,
  series,
  table,
  children,
  actions,
}: ChartFrameProps) {
  const [view, setView] = useState<'chart' | 'table'>('chart');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)', minWidth: 0 }}>
      {(title || actions) && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--s-3)', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 140 }}>
            {title && <h3 style={{ font: 'var(--t-headline)' }}>{title}</h3>}
            {subtitle && (
              <p style={{ font: 'var(--t-caption)', color: 'var(--text-secondary)', marginTop: 2 }}>{subtitle}</p>
            )}
          </div>
          <div style={{ display: 'flex', gap: 'var(--s-2)', alignItems: 'center' }}>
            {actions}
            <SegmentedControl
              size="sm"
              value={view}
              onChange={setView}
              options={[
                { value: 'chart', label: 'Graphique' },
                { value: 'table', label: 'Tableau' },
              ]}
            />
          </div>
        </div>
      )}

      {view === 'chart' ? (
        <>
          <div style={{ overflowX: 'auto', minWidth: 0 }}>{children}</div>
          {series.length >= 2 && <Legend series={series} />}
        </>
      ) : (
        <div style={{ overflowX: 'auto', minWidth: 0 }}>{table}</div>
      )}
    </div>
  );
}

export function Legend({ series }: { series: ChartSeries[] }) {
  return (
    <ul style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-3)', font: 'var(--t-caption)' }}>
      {series.map((item, index) => (
        <li key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)' }}>
          <span
            aria-hidden
            style={{
              width: 10,
              height: 10,
              borderRadius: 'var(--r-xs)',
              background: item.color ?? seriesColor(index),
              flexShrink: 0,
            }}
          />
          {item.label}
        </li>
      ))}
    </ul>
  );
}

/** Tableau générique servant de vue de secours aux graphiques. */
export function DataTable({
  columns,
  rows,
}: {
  columns: string[];
  rows: Array<Array<ReactNode>>;
}) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', font: 'var(--t-footnote)' }}>
      <thead>
        <tr>
          {columns.map((column, i) => (
            <th
              key={column}
              scope="col"
              style={{
                textAlign: i === 0 ? 'left' : 'right',
                padding: '6px 8px',
                borderBottom: '1px solid var(--border)',
                color: 'var(--text-secondary)',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {row.map((cell, cellIndex) => (
              <td
                key={cellIndex}
                style={{
                  textAlign: cellIndex === 0 ? 'left' : 'right',
                  padding: '6px 8px',
                  borderBottom: '1px solid var(--separator)',
                  fontVariantNumeric: cellIndex === 0 ? undefined : 'tabular-nums',
                }}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Infobulle de survol
// ---------------------------------------------------------------------------

interface HoverState {
  x: number;
  y: number;
  content: ReactNode;
}

function ChartTooltip({ hover }: { hover: HoverState | null }) {
  if (!hover) return null;
  return (
    <div
      role="tooltip"
      style={{
        position: 'absolute',
        left: hover.x,
        top: hover.y,
        transform: 'translate(-50%, calc(-100% - 10px))',
        padding: '6px 9px',
        borderRadius: 'var(--r-sm)',
        background: 'var(--glass)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-md)',
        font: 'var(--t-caption)',
        color: 'var(--text)',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        zIndex: 2,
      }}
    >
      {hover.content}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Colonnes (une ou plusieurs séries, groupées ou empilées)
// ---------------------------------------------------------------------------

export interface ColumnDatum {
  label: string;
  /** Une valeur par série, dans l'ordre de `series`. */
  values: number[];
  /** Étiquette courte affichée sous la colonne (sinon `label` tronqué). */
  tick?: string;
}

export function ColumnChart({
  data,
  series,
  height = 200,
  stacked = false,
  formatValue = formatCompact,
  unit = '',
  labelExtremes = true,
}: {
  data: ColumnDatum[];
  series: ChartSeries[];
  height?: number;
  stacked?: boolean;
  formatValue?: (value: number) => string;
  unit?: string;
  /** Étiquette directe sur la valeur la plus haute uniquement. */
  labelExtremes?: boolean;
}) {
  const [hover, setHover] = useState<HoverState | null>(null);
  const clipId = useId();

  const padding = { top: 18, right: 8, bottom: 26, left: 40 };
  const innerHeight = height - padding.top - padding.bottom;
  const width = Math.max(260, data.length * (stacked ? 46 : Math.max(38, series.length * 20)) + padding.left + padding.right);
  const innerWidth = width - padding.left - padding.right;

  const totals = data.map((d) => (stacked ? d.values.reduce((s, v) => s + v, 0) : Math.max(0, ...d.values)));
  const scale = niceScale(Math.max(...totals, 1));
  const y = (value: number) => padding.top + innerHeight * (1 - value / scale.max);

  const bandWidth = innerWidth / Math.max(1, data.length);
  const barMax = 24;
  const groupCount = stacked ? 1 : series.length;
  const barWidth = Math.min(barMax, (bandWidth * 0.62) / groupCount);
  const peakIndex = totals.indexOf(Math.max(...totals));

  return (
    <div style={{ position: 'relative' }}>
      <svg width={width} height={height} role="img" style={{ display: 'block', overflow: 'visible' }}>
        <defs>
          {/* Coins arrondis uniquement du côté de la valeur : un rect masqué par
              un rect à coins arrondis décalé sous la ligne de base. */}
          <clipPath id={`${clipId}-top`}>
            <rect x={0} y={0} width={width} height={height} />
          </clipPath>
        </defs>

        {/* Grille + graduations */}
        {scale.values.map((value) => (
          <g key={value}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={y(value)}
              y2={y(value)}
              stroke={value === 0 ? 'var(--viz-axis)' : 'var(--viz-grid)'}
              strokeWidth={1}
            />
            <text
              x={padding.left - 6}
              y={y(value) + 3.5}
              textAnchor="end"
              style={{ font: 'var(--t-caption-2)', fill: 'var(--viz-muted)', fontVariantNumeric: 'tabular-nums' }}
            >
              {formatCompact(value)}
            </text>
          </g>
        ))}

        {data.map((datum, index) => {
          const bandStart = padding.left + index * bandWidth;
          let stackTop = 0;

          return (
            <g key={`${datum.label}-${index}`}>
              {series.map((serie, serieIndex) => {
                const value = datum.values[serieIndex] ?? 0;
                if (value <= 0) return null;

                const color = serie.color ?? seriesColor(serieIndex);
                let barX: number;
                let barY: number;
                let barHeight: number;

                if (stacked) {
                  barX = bandStart + (bandWidth - barWidth) / 2;
                  barY = y(stackTop + value);
                  barHeight = innerHeight * (value / scale.max);
                  stackTop += value;
                } else {
                  const groupWidth = barWidth * series.length + 2 * (series.length - 1);
                  barX = bandStart + (bandWidth - groupWidth) / 2 + serieIndex * (barWidth + 2);
                  barY = y(value);
                  barHeight = innerHeight * (value / scale.max);
                }

                // L'écart de 2 px entre segments empilés est obtenu en réduisant
                // la hauteur, pas en dessinant un contour.
                const drawHeight = stacked ? Math.max(1, barHeight - 2) : Math.max(1, barHeight);
                const radius = Math.min(4, drawHeight / 2, barWidth / 2);
                const isTopSegment = !stacked || serieIndex === series.length - 1 ||
                  datum.values.slice(serieIndex + 1).every((v) => (v ?? 0) <= 0);

                return (
                  <g key={serie.label}>
                    <path
                      d={
                        isTopSegment
                          ? roundedTopRect(barX, barY, barWidth, drawHeight, radius)
                          : `M${barX} ${barY}h${barWidth}v${drawHeight}h${-barWidth}Z`
                      }
                      fill={color}
                      clipPath={`url(#${clipId}-top)`}
                      style={{ transition: 'opacity var(--d-fast)' }}
                      onMouseEnter={(event) =>
                        setHover({
                          x: barX + barWidth / 2,
                          y: barY,
                          content: (
                            <>
                              <strong>{datum.label}</strong>
                              <br />
                              {series.length > 1 ? `${serie.label} : ` : ''}
                              {formatValue(value)}
                              {unit}
                            </>
                          ),
                        })
                      }
                      onMouseLeave={() => setHover(null)}
                    />
                  </g>
                );
              })}

              {/* Étiquette directe : seulement sur l'extrême, jamais partout. */}
              {labelExtremes && index === peakIndex && totals[index]! > 0 && (
                <text
                  x={bandStart + bandWidth / 2}
                  y={y(totals[index]!) - 6}
                  textAnchor="middle"
                  style={{ font: 'var(--t-caption-2)', fill: 'var(--text-secondary)', fontWeight: 600 }}
                >
                  {formatValue(totals[index]!)}
                  {unit}
                </text>
              )}

              <text
                x={bandStart + bandWidth / 2}
                y={height - padding.bottom + 15}
                textAnchor="middle"
                style={{ font: 'var(--t-caption-2)', fill: 'var(--viz-muted)' }}
              >
                {datum.tick ?? (datum.label.length > 6 ? `${datum.label.slice(0, 5)}…` : datum.label)}
              </text>
            </g>
          );
        })}
      </svg>
      <ChartTooltip hover={hover} />
    </div>
  );
}

/** Rectangle à sommet arrondi et base carrée (spec des marques). */
function roundedTopRect(x: number, y: number, width: number, height: number, radius: number): string {
  const r = Math.max(0, Math.min(radius, width / 2, height));
  return [
    `M${x} ${y + height}`,
    `V${y + r}`,
    `Q${x} ${y} ${x + r} ${y}`,
    `H${x + width - r}`,
    `Q${x + width} ${y} ${x + width} ${y + r}`,
    `V${y + height}`,
    'Z',
  ].join('');
}

// ---------------------------------------------------------------------------
// Ligne / aire avec réticule
// ---------------------------------------------------------------------------

export interface LinePoint {
  label: string;
  values: Array<number | null>;
}

export function LineChart({
  data,
  series,
  height = 210,
  area = false,
  formatValue = formatCompact,
  unit = '',
  yMax,
}: {
  data: LinePoint[];
  series: ChartSeries[];
  height?: number;
  area?: boolean;
  formatValue?: (value: number) => string;
  unit?: string;
  yMax?: number;
}) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const gradientId = useId();

  const padding = { top: 18, right: 16, bottom: 26, left: 40 };
  const width = Math.max(280, data.length * 54 + padding.left + padding.right);
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  const allValues = data.flatMap((point) => point.values.filter((v): v is number => v != null));
  const scale = niceScale(yMax ?? Math.max(...allValues, 1));

  const x = (index: number) => padding.left + (data.length <= 1 ? innerWidth / 2 : (innerWidth * index) / (data.length - 1));
  const y = (value: number) => padding.top + innerHeight * (1 - value / scale.max);

  const paths = useMemo(
    () =>
      series.map((_, serieIndex) => {
        const segments: string[] = [];
        let open = false;
        data.forEach((point, index) => {
          const value = point.values[serieIndex];
          if (value == null) {
            open = false;
            return;
          }
          segments.push(`${open ? 'L' : 'M'}${x(index).toFixed(2)} ${y(value).toFixed(2)}`);
          open = true;
        });
        return segments.join(' ');
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, series, scale.max, width, height],
  );

  const hovered = hoverIndex != null ? data[hoverIndex] : null;

  return (
    <div style={{ position: 'relative' }}>
      <svg
        width={width}
        height={height}
        role="img"
        style={{ display: 'block', overflow: 'visible' }}
        onMouseLeave={() => setHoverIndex(null)}
        onMouseMove={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          const relative = event.clientX - rect.left - padding.left;
          const ratio = innerWidth > 0 ? relative / innerWidth : 0;
          const index = Math.round(ratio * Math.max(1, data.length - 1));
          setHoverIndex(Math.min(data.length - 1, Math.max(0, index)));
        }}
      >
        <defs>
          {series.map((serie, index) => (
            <linearGradient key={index} id={`${gradientId}-${index}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={serie.color ?? seriesColor(index)} stopOpacity={0.16} />
              <stop offset="100%" stopColor={serie.color ?? seriesColor(index)} stopOpacity={0.01} />
            </linearGradient>
          ))}
        </defs>

        {scale.values.map((value) => (
          <g key={value}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={y(value)}
              y2={y(value)}
              stroke={value === 0 ? 'var(--viz-axis)' : 'var(--viz-grid)'}
              strokeWidth={1}
            />
            <text
              x={padding.left - 6}
              y={y(value) + 3.5}
              textAnchor="end"
              style={{ font: 'var(--t-caption-2)', fill: 'var(--viz-muted)', fontVariantNumeric: 'tabular-nums' }}
            >
              {formatCompact(value)}
            </text>
          </g>
        ))}

        {/* Réticule */}
        {hoverIndex != null && (
          <line
            x1={x(hoverIndex)}
            x2={x(hoverIndex)}
            y1={padding.top}
            y2={height - padding.bottom}
            stroke="var(--viz-axis)"
            strokeWidth={1}
          />
        )}

        {series.map((serie, serieIndex) => {
          const color = serie.color ?? seriesColor(serieIndex);
          const path = paths[serieIndex] ?? '';
          if (!path) return null;
          const lastIndex = [...data].reverse().findIndex((p) => p.values[serieIndex] != null);
          const endIndex = lastIndex < 0 ? -1 : data.length - 1 - lastIndex;

          return (
            <g key={serie.label}>
              {area && (
                <path
                  d={`${path} L${x(data.length - 1)} ${y(0)} L${x(0)} ${y(0)} Z`}
                  fill={`url(#${gradientId}-${serieIndex})`}
                />
              )}
              <path d={path} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />

              {/* Point final : ≥ 8 px, cerclé de la couleur de surface. */}
              {endIndex >= 0 && (
                <circle
                  cx={x(endIndex)}
                  cy={y(data[endIndex]!.values[serieIndex] as number)}
                  r={4}
                  fill={color}
                  stroke="var(--surface)"
                  strokeWidth={2}
                />
              )}

              {hoverIndex != null && data[hoverIndex]?.values[serieIndex] != null && (
                <circle
                  cx={x(hoverIndex)}
                  cy={y(data[hoverIndex]!.values[serieIndex] as number)}
                  r={4.5}
                  fill={color}
                  stroke="var(--surface)"
                  strokeWidth={2}
                />
              )}
            </g>
          );
        })}

        {data.map((point, index) => {
          // Une graduation sur n pour éviter le chevauchement.
          const stride = Math.ceil(data.length / 8);
          if (index % stride !== 0 && index !== data.length - 1) return null;
          return (
            <text
              key={`${point.label}-${index}`}
              x={x(index)}
              y={height - padding.bottom + 15}
              textAnchor={index === 0 ? 'start' : index === data.length - 1 ? 'end' : 'middle'}
              style={{ font: 'var(--t-caption-2)', fill: 'var(--viz-muted)' }}
            >
              {point.label}
            </text>
          );
        })}
      </svg>

      <ChartTooltip
        hover={
          hovered && hoverIndex != null
            ? {
                x: x(hoverIndex),
                y: padding.top,
                content: (
                  <>
                    <strong>{hovered.label}</strong>
                    {series.map((serie, index) => {
                      const value = hovered.values[index];
                      if (value == null) return null;
                      return (
                        <span key={serie.label} style={{ display: 'block' }}>
                          {series.length > 1 ? `${serie.label} : ` : ''}
                          {formatValue(value)}
                          {unit}
                        </span>
                      );
                    })}
                  </>
                ),
              }
            : null
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Anneau (répartition)
// ---------------------------------------------------------------------------

export interface DonutSlice {
  label: string;
  value: number;
  color?: string;
}

export function DonutChart({
  slices,
  size = 168,
  thickness = 22,
  centerLabel,
  centerValue,
  formatValue = formatCompact,
}: {
  slices: DonutSlice[];
  size?: number;
  thickness?: number;
  centerLabel?: string;
  centerValue?: ReactNode;
  formatValue?: (value: number) => string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;

  if (total <= 0) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size,
          height: size,
          borderRadius: '50%',
          border: `${thickness}px solid var(--surface-3)`,
          color: 'var(--text-tertiary)',
          font: 'var(--t-caption)',
          textAlign: 'center',
        }}
      >
        Aucune donnée
      </div>
    );
  }

  let offset = 0;

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }} role="img">
        {slices.map((slice, index) => {
          const fraction = slice.value / total;
          // Un écart de 2 px en couleur de surface sépare les secteurs.
          const gap = slices.length > 1 ? 2 : 0;
          const length = Math.max(0, circumference * fraction - gap);
          const dash = `${length} ${circumference - length}`;
          const element = (
            <circle
              key={slice.label}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={slice.color ?? seriesColor(index)}
              strokeWidth={hover === index ? thickness + 3 : thickness}
              strokeDasharray={dash}
              strokeDashoffset={-circumference * offset}
              style={{ transition: 'stroke-width var(--d-fast) var(--ease)', cursor: 'pointer' }}
              onMouseEnter={() => setHover(index)}
              onMouseLeave={() => setHover(null)}
            />
          );
          offset += fraction;
          return element;
        })}
      </svg>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          textAlign: 'center',
          padding: thickness,
        }}
      >
        {hover != null ? (
          <>
            <span style={{ font: 'var(--t-headline)', fontWeight: 600 }}>
              {formatValue(slices[hover]!.value)}
            </span>
            <span style={{ font: 'var(--t-caption)', color: 'var(--text-secondary)' }}>
              {slices[hover]!.label}
            </span>
          </>
        ) : (
          <>
            {centerValue != null && <span style={{ font: 'var(--t-title-3)', fontWeight: 700 }}>{centerValue}</span>}
            {centerLabel && (
              <span style={{ font: 'var(--t-caption)', color: 'var(--text-secondary)' }}>{centerLabel}</span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sparkline & tuile de statistique
// ---------------------------------------------------------------------------

export function Sparkline({
  values,
  width = 96,
  height = 28,
  color = 'var(--viz-1)',
}: {
  values: number[];
  width?: number;
  height?: number;
  color?: string;
}) {
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const points = values.map((value, index) => {
    const x = (width * index) / (values.length - 1);
    const y = height - 2 - ((value - min) / span) * (height - 4);
    return `${x.toFixed(1)} ${y.toFixed(1)}`;
  });

  return (
    <svg width={width} height={height} aria-hidden style={{ display: 'block', flexShrink: 0 }}>
      <path d={`M${points.join(' L')}`} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <circle
        cx={width}
        cy={height - 2 - ((values[values.length - 1]! - min) / span) * (height - 4)}
        r={2.5}
        fill={color}
        stroke="var(--surface)"
        strokeWidth={1.5}
      />
    </svg>
  );
}

export function StatTile({
  label,
  value,
  delta,
  deltaLabel,
  trend,
  tone = 'var(--viz-1)',
  icon,
  hero = false,
  onClick,
}: {
  label: string;
  value: ReactNode;
  /** Variation signée, en points de pourcentage ou en valeur absolue. */
  delta?: number;
  deltaLabel?: string;
  trend?: number[];
  tone?: string;
  icon?: ReactNode;
  /** Chiffre phare : un seul par écran. */
  hero?: boolean;
  onClick?: () => void;
}) {
  const positive = (delta ?? 0) > 0;
  const negative = (delta ?? 0) < 0;

  return (
    <Card
      padding="md"
      interactive={Boolean(onClick)}
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
      className={cx(onClick && 'bala-card-interactive')}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--s-3)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ font: 'var(--t-footnote)', color: 'var(--text-secondary)' }}>{label}</p>
          <p
            style={{
              font: hero ? '700 48px/1.05 var(--font)' : 'var(--t-title-2)',
              letterSpacing: '-0.02em',
              marginTop: hero ? 'var(--s-2)' : 4,
            }}
          >
            {value}
          </p>
          {delta != null && (
            <p
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                font: 'var(--t-caption)',
                color: positive ? 'var(--green)' : negative ? 'var(--red)' : 'var(--text-secondary)',
                marginTop: 4,
              }}
            >
              <span aria-hidden>{positive ? '↑' : negative ? '↓' : '→'}</span>
              {Math.abs(delta).toLocaleString('fr-BE', { maximumFractionDigits: 1 })}
              {deltaLabel ? ` ${deltaLabel}` : ''}
            </p>
          )}
        </div>

        {icon && (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: 'var(--r-sm)',
              background: 'var(--surface-3)',
              fontSize: 17,
              flexShrink: 0,
            }}
          >
            {icon}
          </span>
        )}
      </div>

      {trend && trend.length > 1 && (
        <div style={{ marginTop: 'var(--s-3)' }}>
          <Sparkline values={trend} color={tone} width={140} height={30} />
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Barres horizontales (classements : taux de présence, dépenses par catégorie)
// ---------------------------------------------------------------------------

export function RankedBars({
  rows,
  formatValue = (value) => formatCompact(value),
  unit = '',
  maxRows,
  tone,
}: {
  rows: Array<{ label: string; value: number; color?: string; hint?: string }>;
  formatValue?: (value: number) => string;
  unit?: string;
  maxRows?: number;
  /** Couleur unique pour toutes les barres (comparaison d'une même mesure). */
  tone?: string;
}) {
  const shown = maxRows ? rows.slice(0, maxRows) : rows;
  const max = Math.max(...shown.map((row) => row.value), 1);

  return (
    <ul style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)' }}>
      {shown.map((row, index) => (
        <li key={`${row.label}-${index}`} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--s-2)', font: 'var(--t-footnote)' }}>
            <span className="bala-truncate" style={{ minWidth: 0 }}>
              {row.label}
              {row.hint && <span style={{ color: 'var(--text-tertiary)' }}> · {row.hint}</span>}
            </span>
            <span style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
              {formatValue(row.value)}
              {unit}
            </span>
          </div>
          <div style={{ height: 8, borderRadius: 'var(--r-full)', background: 'var(--surface-3)', overflow: 'hidden' }}>
            <div
              style={{
                // Voir `ProgressBar` : `scaleX` plutôt que `width`, pour rester
                // sur le compositeur.
                width: '100%',
                height: '100%',
                borderRadius: 'var(--r-full)',
                background: row.color ?? tone ?? seriesColor(index),
                transformOrigin: 'left',
                transform: `scaleX(${(row.value / max) || 0})`,
                transition: 'transform var(--d-slow) var(--ease-out)',
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
